<template>
  <van-popup
    v-model="show"
    class="arrive-photo-popup"
    :close-on-click-overlay="false"
  >
    <div class="arrive-photo-dialog">
      <div class="dialog-header">上传到达现场照片</div>
      <div class="dialog-body">
        <div class="dialog-tips">请上传到达现场的照片，确认后才能标记为已到达</div>
        <div
          v-if="uploadedMedia && !uploading && !uploadError"
          class="upload-panel is-uploaded"
        >
          <img
            class="preview-img"
            :src="uploadedMedia.url"
            alt=""
            @click="previewImage"
          >
          <div
            class="delete-mask"
            @click="clearUpload"
          >删除</div>
        </div>
        <div
          v-else-if="uploadedMedia && !uploading && uploadError"
          class="upload-panel is-uploaded is-error"
          @click="previewImage"
        >
          <img
            class="preview-img"
            :src="uploadedMedia.url"
            alt=""
          >
          <div class="uploading-mask" />
          <img
            class="warning-icon"
            src="@/assets/img/orderGeneral/icon-Warning@2x.png"
            alt=""
          >
          <div class="upload-text">图片上传失败</div>
          <div
            class="retry-mask can-retry"
            @click.stop="retryUpload"
          >点击重新上传</div>
        </div>
        <van-uploader
          v-else
          class="arrive-uploader"
          accept=".jpg,.jpeg,.png,.JPG,.JPEG,.PNG"
          capture="camera"
          :before-read="beforeRead"
          :after-read="afterRead"
        >
          <div
            class="upload-panel"
            :class="{
              'is-uploaded': uploadedMedia,
              'is-error': uploadError,
              'is-uploading': uploading
            }"
            @click="handlePanelClick"
          >
            <template v-if="uploading">
              <img
                v-if="uploadingPreviewUrl"
                class="preview-img"
                :src="uploadingPreviewUrl"
                alt=""
              >
              <div class="uploading-mask" />
              <div class="upload-text">图片上传中</div>
            </template>
            <template v-else-if="uploadError">
              <img
                class="warning-icon"
                src="@/assets/img/orderGeneral/icon-Warning@2x.png"
                alt=""
              >
              <div class="upload-text">图片上传失败</div>
              <div class="retry-mask">点击重新上传</div>
            </template>
            <template v-else>
              <van-icon
                name="plus"
                class="add-icon"
              />
              <div class="upload-text">点击选择照片</div>
            </template>
          </div>
        </van-uploader>
      </div>
      <div class="dialog-footer">
        <button
          type="button"
          class="dialog-btn cancel-btn"
          :disabled="saving"
          @click="close"
        >取消</button>
        <button
          type="button"
          class="dialog-btn confirm-btn"
          :class="{ active: canConfirm }"
          :disabled="!canConfirm || uploading || saving"
          @click="confirm"
        >{{ saving ? '保存中...' : '确认上传' }}</button>
      </div>
    </div>
  </van-popup>
</template>

<script>
import dayjs from 'dayjs';
import Compressor from 'compressorjs';
import { ImagePreview } from 'vant';
import { syncArrivalPhoto } from '@/common/apiGeneral.js';
import { upload, getFileName } from '@/utils/ourOss';

export default {
  name: 'ArrivePhotoUploadDialog',
  props: {
    orderNo: {
      type: String,
      default: ''
    },
    taskNo: {
      type: String,
      default: ''
    },
    useTraceCar: {
      type: [String, Number],
      default: ''
    },
    taskDetail: {
      type: Object,
      default: () => ({})
    },
    currentPosition: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      show: false,
      uploading: false,
      saving: false,
      uploadError: false,
      uploadingPreviewUrl: '',
      uploadedMedia: null,
      uploadTaskId: 0
    };
  },
  computed: {
    canConfirm() {
      return Boolean(this.uploadedMedia && this.uploadedMedia.imgId && this.uploadedMedia.imgUrl);
    }
  },
  methods: {
    open() {
      this.uploadTaskId += 1;
      this.show = true;
      this.uploading = false;
      this.saving = false;
      this.uploadError = false;
      this.uploadingPreviewUrl = '';
      this.uploadedMedia = null;
    },
    close() {
      if (this.saving) {
        return;
      }
      this.uploadTaskId += 1;
      this.show = false;
    },
    beforeRead(file) {
      return new Promise((resolve, reject) => {
        if (this.uploading || this.saving) {
          reject();
          return;
        }

        const ext = file.name.replace(/.+\./, '').toLowerCase();
        if (!['jpg', 'jpeg', 'png'].includes(ext)) {
          this.$toast('上传文件只能是JPG、JPEG、PNG格式');
          reject();
          return;
        }

        if (file.size / 1024 / 1024 >= 10) {
          this.$toast('上传文件大小不能超过 10MB!');
          reject();
          return;
        }

        if (this.getWeightedNameLength(getFileName(file.name)) > 48) {
          this.$toast('文件名称不超过24个汉字');
          reject();
          return;
        }

        if (!this.currentPosition) {
          this.$toast('当前位置获取失败，请确认是否打开位置权限！');
          reject();
          return;
        }

        resolve(file);
      });
    },
    getWeightedNameLength(name) {
      return String(name || '').split('').reduce((sum, char) => {
        return sum + (/[\u4e00-\u9fa5]/.test(char) ? 2 : 1);
      }, 0);
    },
    doCompressor(file, dayjsDate) {
      return new Promise((resolve) => {
        const quality = 0.8;
        const compressor = new Compressor(file, {
          strict: false,
          quality,
          maxWidth: 2160,
          minWidth: 1080,
          drew: (context, canvas) => {
            const fillWidth = canvas.width * 3 / 4;
            let fontBit = 30;
            if (canvas.width > 1080) {
              fontBit = fontBit * canvas.width / 1080;
            }
            const wordLength = Math.ceil(fillWidth / fontBit);
            const lineHeight = 1.5;
            const fillX = canvas.width - fontBit;
            const { formattedAddress, longitude, latitude } = this.currentPosition;
            context.font = `${fontBit}px Roboto, PingFang`;
            context.fillStyle = 'red';
            context.textAlign = 'right';
            context.textBaseline = 'Middle';
            context.fillText(`救援师傅:${this.taskDetail.workerName || ''}`, fillX, fontBit * lineHeight);
            context.fillText(`(H5相机)${dayjsDate.format('YYYY-MM-DD HH:mm:ss')}`, fillX, fontBit * lineHeight * 2);
            const address = formattedAddress || '';
            const lines = Math.max(Math.ceil(address.length / wordLength), 1);
            for (let index = 0; index < lines; index++) {
              context.fillText(address.slice(wordLength * index, wordLength * (index + 1)), fillX, fontBit * lineHeight * (3 + index));
            }
            context.fillText(`经度:${longitude} 纬度:${latitude}`, fillX, fontBit * lineHeight * (3 + lines));
          },
          success: (result) => {
            try {
              resolve(new File([result], file.name, { type: result.type || file.type }));
            } catch (error) {
              result.name = file.name;
              resolve(result);
            }
          },
          error: () => {
            resolve(file);
          }
        });
        console.log(compressor);
      });
    },
    fileToDataUrl(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          resolve(event.target.result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },
    async afterRead({ file, content }) {
      const taskId = this.uploadTaskId + 1;
      this.uploadTaskId = taskId;
      this.uploadError = false;
      this.uploading = true;
      this.uploadingPreviewUrl = content || '';
      this.uploadedMedia = null;
      try {
        const today = dayjs();
        const realFile = await this.doCompressor(file, today);
        if (!this.isCurrentUploadTask(taskId)) {
          return;
        }
        const previewUrl = await this.fileToDataUrl(realFile);
        if (!this.isCurrentUploadTask(taskId)) {
          return;
        }
        this.uploadedMedia = {
          url: previewUrl,
          file: realFile,
          fileName: file.name,
          fileType: this.getFileType(file.name),
          uploadFrom: 'H5上传',
          watermarkTime: today.format('YYYY-MM-DD HH:mm:ss')
        };
        this.uploadingPreviewUrl = previewUrl;
        await this.uploadImageToMediaService(taskId);
      } catch (error) {
        console.log(error);
        if (this.isCurrentUploadTask(taskId)) {
          this.uploadError = true;
          this.$toast('图片处理失败');
        }
      } finally {
        if (this.isCurrentUploadTask(taskId)) {
          this.uploading = false;
          this.uploadingPreviewUrl = '';
        }
      }
    },
    isCurrentUploadTask(taskId) {
      return this.show && this.uploadTaskId === taskId;
    },
    clearUpload() {
      this.uploadedMedia = null;
      this.uploadError = false;
    },
    previewImage() {
      if (!this.uploadedMedia) {
        return;
      }
      ImagePreview([this.uploadedMedia.url]);
    },
    handlePanelClick() {
      if (this.uploadError && this.uploadedMedia && !this.uploading) {
        this.previewImage();
      }
    },
    retryUpload() {
      if (!(this.uploadedMedia && !this.uploading && !this.saving)) {
        return;
      }
      this.uploadImageToMediaService();
    },
    async uploadImageToMediaService(taskId) {
      const currentTaskId = taskId || this.uploadTaskId + 1;
      this.uploadTaskId = currentTaskId;
      this.uploading = true;
      this.uploadError = false;
      this.uploadingPreviewUrl = this.uploadedMedia.url;
      this.uploadedMedia = {
        ...this.uploadedMedia,
        imgId: '',
        imgUrl: ''
      };
      try {
        const res = await upload(this.uploadedMedia.file);
        if (!this.isCurrentUploadTask(currentTaskId)) {
          return false;
        }
        if (!(res && res.data && String(res.data.code) === '0')) {
          this.$toast(res && res.data && res.data.message || '上传失败');
          this.uploadError = true;
          return false;
        }
        const data = res.data.data || {};
        const uploadInfo = this.getUploadImageInfo(data);

        if (!uploadInfo.imgId) {
          this.$toast('上传结果缺少图片信息');
          this.uploadError = true;
          return false;
        }

        this.uploadedMedia = {
          ...this.uploadedMedia,
          ...uploadInfo
        };
        return true;
      } catch (error) {
        console.log(error);
        if (this.isCurrentUploadTask(currentTaskId)) {
          this.uploadError = true;
          this.$toast('上传失败');
        }
        return false;
      } finally {
        if (this.isCurrentUploadTask(currentTaskId)) {
          this.uploading = false;
          this.uploadingPreviewUrl = '';
        }
      }
    },
    getFileType(fileName = '') {
      const arr = fileName.split('.');
      const suffix = arr[arr.length - 1].toLowerCase();
      if (['mp4'].includes(suffix)) {
        return 'video';
      }
      if (['mp3', 'm4a'].includes(suffix)) {
        return 'audio';
      }
      return 'image';
    },
    getUploadImageInfo(data = {}) {
      const imgId = data.picId || data.imgId || data.fileId || data.id || data.fileKey || data.ossId;
      const imgUrl = data.imgUrl ||
        data.accessUrl ||
        data.fileUrl ||
        data.picUrl ||
        data.url ||
        data.fullUrl ||
        data.ossUrl ||
        data.path ||
        data.filePath ||
        data.picPath ||
        data.objectUrl ||
        imgId;
      return {
        imgId,
        imgUrl
      };
    },
    async confirm() {
      if (!this.canConfirm || this.uploading || this.saving) {
        return;
      }
      const taskId = this.uploadTaskId + 1;
      const taskNo = this.taskNo || this.taskDetail.taskNo;
      this.uploadTaskId = taskId;
      this.saving = true;
      this.$toast.loading({
        message: '加载中...',
        forbidClick: true
      });
      try {
        await syncArrivalPhoto({
          taskNo,
          imgId: this.uploadedMedia.imgId,
          imgUrl: this.uploadedMedia.imgUrl,
          fileType: this.uploadedMedia.fileType,
          uploadFrom: this.uploadedMedia.uploadFrom
        });
        if (!this.isCurrentUploadTask(taskId)) {
          return;
        }
        this.$toast.clear();
        this.close();
        this.$emit('uploaded', this.uploadedMedia);
      } catch (error) {
        console.log('error:', error);
        if (this.isCurrentUploadTask(taskId)) {
          this.$toast.clear();
          this.$toast(error && error.data && error.data.message || '上传失败');
        }
      } finally {
        if (this.isCurrentUploadTask(taskId)) {
          this.saving = false;
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.arrive-photo-popup {
  overflow: visible;

  border-radius: 8px;
  background: transparent;
}

.arrive-photo-dialog {
  overflow: hidden;

  width: 303px;
  height: 368px;

  border-radius: 8px;
  background: #FFFFFF;
}

.dialog-header {
  height: 50px;

  text-align: center;

  color: #383838;
  box-shadow: inset 0 -1px 0 0 #E6E6E6;

  font-family: PingFangSC-Semibold;
  font-size: 16px;
  font-weight: 600;
  line-height: 50px;
}

.dialog-body {
  padding: 15px 16px 0;
}

.dialog-tips {
  margin-bottom: 16px;

  text-align: center;
  letter-spacing: 0;

  color: #FF8F1F;

  font-family: PingFangSC-Semibold;
  font-size: 12px;
  font-weight: 600;
  line-height: 17px;
}

.arrive-uploader {
  width: 271px;
  height: 181px;

  ::v-deep {
    .van-uploader__wrapper,
    .van-uploader__input-wrapper,
    .van-uploader__preview,
    .van-uploader__preview-image {
      width: 271px;
      height: 181px;
      margin: 0;
    }

    .van-uploader__preview {
      display: none;
    }
  }
}

.upload-panel {
  position: relative;

  display: flex;
  overflow: hidden;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  width: 271px;
  height: 181px;

  border: 1px solid #B1BED7;
  border-radius: 8px;
  background: #F2F4FA;
}

.add-icon {
  color: #B1BED7;

  font-size: 22px;
}

.warning-icon {
  width: 20px;
  height: 20px;
}

.upload-text {
  margin-top: 8px;

  text-align: center;

  color: #B1BED7;

  font-family: PingFangSC-Regular;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.is-error {
  .preview-img {
    position: absolute;
    z-index: 0;
    top: 0;
    left: 0;
  }

  .uploading-mask {
    pointer-events: none;

    background: rgba(0, 0, 0, 0.8);
  }

  .warning-icon,
  .upload-text {
    position: relative;
    z-index: 2;
  }

  .upload-text {
    color: #F32222;
  }
}

.is-uploading {
  border: 0;

  pointer-events: none;

  .upload-text {
    position: absolute;
    z-index: 2;
    top: 50%;
    left: 50%;

    margin-top: 0;

    transform: translate(-50%, -50%);

    color: #FFFFFF;
  }
}

.uploading-mask {
  position: absolute;
  z-index: 1;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  background: rgba(0, 0, 0, 0.5);
}

.preview-img {
  width: 100%;
  height: 100%;

  object-fit: cover;
}

.delete-mask,
.retry-mask {
  position: absolute;
  z-index: 2;
  right: 0;
  bottom: 0;
  left: 0;

  height: 32px;

  text-align: center;

  color: #FFFFFF;
  background: rgba(0, 0, 0, 0.65);

  font-size: 14px;
  line-height: 32px;
}

.retry-mask {
  letter-spacing: 0;

  pointer-events: none;
}

.can-retry {
  pointer-events: auto;
}

.dialog-footer {
  display: flex;
  justify-content: space-between;

  padding: 24px 16px 0;
}

.dialog-btn {
  width: 127px;
  height: 40px;
  padding: 0;

  border: 0;
  border-radius: 8px;

  font-family: PingFangSC-Regular;
  font-size: 16px;
  line-height: 40px;
}

.cancel-btn {
  color: #5A5E63;
  background: #EDF2F7;
}

.confirm-btn {
  color: #FFFFFF;
  background: #B3D5FF;

  &.active {
    background: #3399FF;
  }
}
</style>
